import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { Injectable, inject } from '@angular/core';
import { type FormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { AlertService } from '@modules/alert/services/alert.service';
import { EventParticipantRole } from '@sharedlib/common/types/event-participant-role';
import { InvitationAcceptStatus } from '@sharedlib/common/types/invitation-accept-status';
import { Objects } from '@sharedlib/common/lang/Objects';
import type { EventParticipantDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';

import { type CalendarEventGroupConfig } from '../types/calendar-event';
import { EventInviteService } from './event-invite.service';

/**
 * Result of the invite confirmation flow.
 *
 *   send — user confirmed sending invitations (or update emails)
 *   skip — user declined, or no prompt was required
 *
 * If the user dismisses the alert without choosing a button, the observable
 * simply never emits and never completes (it will be torn down by the
 * caller's `takeUntil`). Callers therefore don't need a cancel branch —
 * no emission means nothing happens, which is the desired outcome.
 */
export type InviteChoice = 'send' | 'skip';

type EventFormRawValue = ReturnType<FormGroup<CalendarEventGroupConfig>['getRawValue']>;

interface AskIfNeededArgs {
  form: FormGroup<CalendarEventGroupConfig>;
  /** Baseline form snapshot captured when the form was loaded. Required for
   *  the "existing event" branch to detect already-pending participants and
   *  invitation-relevant changes. Callers may pass `null`/`undefined`
   *  (create-only callers like the quick-create popover); the service narrows
   *  internally. */
  originalFormValue?: object | null;
  isRemoteEvent: boolean;
  /** `true` when editing an existing event (stitchItem.id present),
   *  `false` when creating a new event. */
  isExistingEvent: boolean;
}

/**
 * Fields included in invitation emails. Changes to these fields should prompt
 * the "Send updates to participants?" dialog for existing events.
 * Derived from EventInvitationTemplateData in event-invitation-mailer.service.ts.
 *
 * `recurrence` belongs here for the same reason as the rest: the email renders the cadence
 * ("Repeats: Every week on Tuesday for 8 times") and the ICS carries the RRULE, so a rule change
 * that skips the prompt leaves the attendee's calendar on the OLD pattern with no notification —
 * while the backend still counts it as a significant revision and bumps SEQUENCE (SBUX-729).
 * Compared as a string like every other field: re-picking the *same* cadence can re-serialize the
 * rule differently and prompt for an effectively unchanged pattern, which is acceptable — the user
 * deliberately touched the control, and the resulting email simply restates the schedule.
 */
const INVITATION_RELEVANT_FIELDS: ReadonlySet<keyof CalendarEventGroupConfig> = new Set<keyof CalendarEventGroupConfig>(
  [
    'title',
    'description',
    'startDate',
    'startTime',
    'endDate',
    'endTime',
    'allDay',
    'timeZone',
    'location',
    'meetingLink',
    'participants',
    'recurrence',
  ] as const,
);

function isEventFormSnapshot(value: object | null | undefined): value is EventFormRawValue {
  return !!value && 'participants' in value && 'title' in value;
}

/**
 * Shared UI + policy for the "send invitation to participants?" confirmation
 * dialog. Consumed by both the full-form editor (EventFormComponent) and the
 * calendar-grid quick-create popover (EventPopoverFormComponent) so they share
 * identical copy, buttons, and participant-detection rules.
 */
@Injectable()
export class EventInviteConfirmationService {
  private readonly alertService = inject(AlertService);
  private readonly eventInviteService = inject(EventInviteService);

  /**
   * Decide whether an invite/update confirmation dialog is needed and, if so,
   * display it. Emits the user's choice exactly once, then completes.
   *
   * Emits:
   *   'skip' — no prompt needed, or user clicked "Don't send"
   *   'send' — user clicked "Send"
   *
   * If the user dismisses the alert without clicking a button, the observable
   * never emits and never completes (caller's `takeUntil` handles teardown).
   */
  public askIfNeeded(args: AskIfNeededArgs): Observable<InviteChoice> {
    if (args.isRemoteEvent) {
      return of('skip' as const);
    }

    const original = args.originalFormValue;
    const snapshot: EventFormRawValue | undefined = isEventFormSnapshot(original) ? original : undefined;

    if (this.hasPendingParticipants(args.form, snapshot)) {
      return this.showConfirmation({ isPending: true });
    }

    if (
      args.isExistingEvent &&
      this.hasNonOrganizerParticipants(args.form) &&
      this.hasInvitationRelevantChanges(args.form, snapshot)
    ) {
      return this.showConfirmation({ isPending: false });
    }

    return of('skip' as const);
  }

  /**
   * Post-save side effect: send invitations for the just-saved event.
   * Callers that need refreshed participant statuses should chain their
   * own `eventsService.getItem()` afterward (EventsService is component-scoped).
   */
  public sendInvites(eventId: string, config?: StitchRequestConfig): Observable<void> {
    const send = config
      ? this.eventInviteService.sendInvites(eventId, config)
      : this.eventInviteService.sendInvites(eventId);
    return send.pipe(map(() => undefined));
  }

  /**
   * Detects whether the user added NEW participants in this editing session.
   * Participants that were already PENDING when the form loaded must not
   * re-trigger the "Send Invitation" dialog on every save.
   */
  private hasPendingParticipants(
    form: FormGroup<CalendarEventGroupConfig>,
    originalFormValue: EventFormRawValue | undefined,
  ): boolean {
    const participants: EventParticipantDto[] = form.get('participants')?.value || [];
    const currentPending = participants.filter((p) => p.invitationAcceptStatus === InvitationAcceptStatus.PENDING);
    if (currentPending.length === 0) {
      return false;
    }

    if (!isEventFormSnapshot(originalFormValue)) {
      return true;
    }
    const originalParticipants = originalFormValue.participants;
    if (!originalParticipants) {
      return true;
    }

    const originalPendingEmails = new Set(
      originalParticipants
        .filter((p) => p.invitationAcceptStatus === InvitationAcceptStatus.PENDING)
        .map((p) => p.email),
    );

    return currentPending.some((p) => !originalPendingEmails.has(p.email));
  }

  private hasNonOrganizerParticipants(form: FormGroup<CalendarEventGroupConfig>): boolean {
    const participants: EventParticipantDto[] = form.get('participants')?.value || [];
    return participants.some((p) => p.role !== EventParticipantRole.ORGANISER);
  }

  /**
   * Compares the current form value against `originalFormValue` and returns
   * `true` when at least one invitation-relevant field has been modified.
   */
  private hasInvitationRelevantChanges(
    form: FormGroup<CalendarEventGroupConfig>,
    originalFormValue: EventFormRawValue | undefined,
  ): boolean {
    if (!isEventFormSnapshot(originalFormValue)) {
      return true;
    }
    const current = form.getRawValue();
    for (const field of INVITATION_RELEVANT_FIELDS) {
      if (!Objects.equals(current[field], originalFormValue[field])) {
        return true;
      }
    }
    return false;
  }

  private showConfirmation(opts: { isPending: boolean }): Observable<InviteChoice> {
    return new Observable<InviteChoice>((observer) => {
      let resolved = false;
      const resolve = (choice: InviteChoice): void => {
        if (resolved) {
          return;
        }
        resolved = true;
        observer.next(choice);
        observer.complete();
      };

      this.alertService.show({
        title: opts.isPending ? 'Send Invitation' : 'Send updates to participants?',
        body: opts.isPending
          ? 'An invitation will be sent to added participants.'
          : 'An update will be sent to all participants.',
        appearance: 'prompt',
        rightButtons: [
          {
            title: "Don't send",
            appearance: 'secondary',
            close: true,
            click: () => resolve('skip'),
          },
          {
            title: 'Send',
            appearance: 'primary',
            close: true,
            click: () => resolve('send'),
          },
        ],
      });
    });
  }
}
